import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Grid3x3, Search, Heart, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";

/**
 * Sticky mobile bottom navigation bar (Home / Catalog / Search / Wishlist / Cart).
 * Hidden on >= md breakpoint and on admin routes.
 */
export default function MobileBottomNav({ onSearchClick }) {
  const { pathname } = useLocation();
  const { count: cartCount } = useCart();
  const { count: wishlistCount } = useWishlist();

  // Hide on admin and checkout/order/thank-you flows for focus
  if (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/order/")
  ) {
    return null;
  }

  const items = [
    { to: "/", label: "Home", icon: Home, end: true, testId: "mob-nav-home" },
    { to: "/catalog", label: "Shop", icon: Grid3x3, testId: "mob-nav-catalog" },
    { search: true, label: "Search", icon: Search, testId: "mob-nav-search" },
    { to: "/wishlist", label: "Saved", icon: Heart, badge: wishlistCount, testId: "mob-nav-wishlist" },
    { to: "/cart", label: "Bag", icon: ShoppingBag, badge: cartCount, testId: "mob-nav-cart" },
  ];

  return (
    <>
      {/* Spacer so content isn't hidden behind the bar */}
      <div className="h-16 md:hidden" aria-hidden="true" />
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#E6D4CC] bg-white/95 backdrop-blur-md md:hidden"
        data-testid="mobile-bottom-nav"
        aria-label="Primary mobile"
      >
        <ul className="grid grid-cols-5">
          {items.map((it) => {
            const Icon = it.icon;
            const inner = (active) => (
              <span
                className={`flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
                  active ? "text-[#2B2127]" : "text-[#6B5158]"
                }`}
              >
                <span className="relative">
                  <Icon
                    size={20}
                    strokeWidth={active ? 1.8 : 1.4}
                    className={active ? "fill-[#9D5C6C]/10" : ""}
                  />
                  {it.badge > 0 && (
                    <span className="absolute -right-1.5 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#2B2127] px-1 text-[10px] font-medium text-white" data-testid={`${it.testId}-badge`}>
                      {it.badge > 99 ? "99+" : it.badge}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium tracking-wide">{it.label}</span>
              </span>
            );
            if (it.search) {
              return (
                <li key="search">
                  <button
                    type="button"
                    onClick={onSearchClick}
                    className="w-full"
                    data-testid={it.testId}
                    aria-label="Open search"
                  >
                    {inner(false)}
                  </button>
                </li>
              );
            }
            return (
              <li key={it.to}>
                <NavLink to={it.to} end={it.end} data-testid={it.testId}>
                  {({ isActive }) => inner(isActive)}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
